// Настройки ИИ — только владелец платформы (is_superuser).
//
// Ключ вводим мы и привязываем к клиенту; у администратора клиента этой
// настройки нет вообще — ни пункта меню, ни ключа. Ключ сервер наружу не
// отдаёт: здесь только маска. Пустое поле «Ключ» при сохранении = не менять.
//
// Строка «По умолчанию для всех» действует на всех клиентов без своей строки.
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { aiAdminAPI } from '../api';
import toast from 'react-hot-toast';

const INP = {
  width: '100%', padding: '9px 12px', background: 'var(--bg)',
  border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
  fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none',
};
const BTN = (bg, extra = {}) => ({
  background: bg, border: '1px solid ' + bg, borderRadius: 8, color: '#fff',
  padding: '9px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'inherit', ...extra,
});
const GHOST = { ...BTN('transparent'), color: 'var(--muted)', borderColor: 'var(--border)' };
const TH = { textAlign: 'left', padding: '8px 10px', color: 'var(--muted)', fontSize: 12, fontWeight: 600 };
const TD = { padding: '9px 10px', borderTop: '1px solid var(--border)', fontSize: 13.5 };

const fmt = (s) => (s ? new Date(s).toLocaleString('ru-RU') : '');
const errText = (e) => (e && e.response && e.response.data && e.response.data.detail) || 'Не удалось выполнить';

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function EditForm({ row, presets, onClose, onChanged }) {
  const target = row.id ? String(row.id) : 'default';
  const [f, setF] = useState({
    enabled: row.enabled, provider: row.provider, base_url: row.base_url,
    model: row.model, daily_limit: row.daily_limit, api_key: '',
  });
  const [cur, setCur] = useState(row);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);

  useEffect(() => {
    aiAdminAPI.get(target).then(r => setLog(r.data.log || [])).catch(() => {});
  }, [target]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const preset = presets.find(p => p.id === f.provider) || {};
  const custom = f.provider === 'openai_compatible';

  const changeProvider = (id) => {
    const p = presets.find(x => x.id === id) || {};
    setF(prev => ({ ...prev, provider: id, base_url: p.base_url || '', model: p.model || '' }));
  };

  const body = () => ({
    enabled: f.enabled, provider: f.provider, base_url: f.base_url,
    model: f.model, daily_limit: f.daily_limit, api_key: f.api_key,
  });

  const save = async () => {
    setBusy(true);
    try {
      const r = await aiAdminAPI.save(target, body());
      setCur(r.data); setLog(r.data.log || []); set('api_key', '');
      toast.success('Сохранено');
      onChanged();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const check = async () => {
    setBusy(true); setTest(null);
    try {
      const r = await aiAdminAPI.test(target, body());
      setTest(r.data);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const resetKey = async () => {
    if (!window.confirm('Сбросить ключ? ИИ у этого клиента перестанет отвечать, пока не введёте новый.')) return;
    setBusy(true);
    try {
      const r = await aiAdminAPI.resetKey(target);
      setCur(r.data); setLog(r.data.log || []);
      toast.success('Ключ сброшен');
      onChanged();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div style={{ background: 'var(--surface, var(--bg))', border: '1px solid var(--border)',
                  borderRadius: 12, padding: 18, marginTop: 14, maxWidth: 560 }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>{row.name}</div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, fontSize: 14 }}>
        <input type="checkbox" checked={f.enabled} onChange={e => set('enabled', e.target.checked)} />
        Включено
      </label>
      <Field label="Провайдер">
        <select style={INP} value={f.provider} onChange={e => changeProvider(e.target.value)}>
          {presets.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </Field>
      <Field label={custom ? 'Адрес сервиса (base URL)' : 'Адрес сервиса (из пресета)'}>
        <input style={INP} value={f.base_url} disabled={!custom}
               placeholder={preset.base_url || 'https://…/v1'}
               onChange={e => set('base_url', e.target.value)} />
      </Field>
      <Field label="Модель">
        <input style={INP} value={f.model} placeholder={preset.model || ''}
               onChange={e => set('model', e.target.value)} />
      </Field>
      {cur.key_broken && (
        <div style={{color: '#b91c1c', fontWeight: 600, marginBottom: 6}}>
          Ключ не читается — введите заново
        </div>
      )}
      <Field label={'Ключ' + (cur.key_broken ? ' — не читается' : cur.key_set ? ' — сейчас ' + cur.key_hint : ' — не задан')}>
        <input style={INP} type="password" autoComplete="new-password" value={f.api_key}
               placeholder={cur.key_set ? 'Оставьте пустым, чтобы не менять' : 'Вставьте ключ'}
               onChange={e => set('api_key', e.target.value)} />
      </Field>
      <Field label="Лимит вопросов в сутки (на пользователя)">
        <input style={INP} type="number" min="1" value={f.daily_limit}
               onChange={e => set('daily_limit', e.target.value)} />
      </Field>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button style={BTN('var(--accent)')} disabled={busy} onClick={save}>Сохранить</button>
        <button style={GHOST} disabled={busy} onClick={check}>Проверить связь</button>
        {cur.key_set && <button style={GHOST} disabled={busy} onClick={resetKey}>Сбросить ключ</button>}
        <button style={GHOST} onClick={onClose}>Закрыть</button>
      </div>
      {test && (
        <div style={{ marginTop: 12, fontSize: 13.5, color: test.ok ? '#16a34a' : '#dc2626' }}>
          {test.ok ? 'Связь есть: модель ответила (' + test.model + ')' : 'Ошибка: ' + test.error}
        </div>
      )}
      {cur.updated_at && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>
          Изменено: {fmt(cur.updated_at)} {cur.updated_by}
        </div>
      )}
      {log.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
          {log.map((l, i) => (
            <div key={i}>{fmt(l.at)} · {l.who || '—'} · {l.action}{l.details ? ': ' + l.details : ''}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AiSettings() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);   // 'default' | id клиента

  const load = useCallback(() => {
    aiAdminAPI.list().then(r => setData(r.data)).catch(e => toast.error(errText(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = data ? [{ ...data.default, id: null, name: 'По умолчанию для всех' }, ...data.orgs] : [];
  const keyOf = (r) => (r.id === null ? 'default' : String(r.id));

  return (
    <Layout title="Настройки ИИ">
      <div style={{ maxWidth: 1000 }}>
        <div style={{ background: 'var(--accent-weak)', border: '1px solid var(--border)',
                      borderRadius: 10, padding: '11px 15px', marginBottom: 16, fontSize: 12.5,
                      color: 'var(--muted)', lineHeight: 1.6 }}>
          Ключ ИИ вводит владелец платформы и привязывает к клиенту; администратор клиента
          эту настройку и ключ не видит. У клиента без своей строки действует строка
          «По умолчанию для всех».
        </div>
        {!data && <div style={{ color: 'var(--muted)' }}>Загрузка…</div>}
        {data && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={TH}>Клиент</th><th style={TH}>Состояние</th><th style={TH}>Провайдер</th>
                  <th style={TH}>Модель</th><th style={TH}>Ключ</th><th style={TH}>Лимит/сутки</th><th style={TH} />
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={keyOf(r)}>
                    <td style={{ ...TD, fontWeight: r.id === null ? 700 : 500 }}>{r.name}</td>
                    <td style={{ ...TD, color: r.enabled ? '#16a34a' : 'var(--muted)' }}>
                      {r.exists ? (r.enabled ? 'Включено' : 'Выключено') : 'Не задано'}
                    </td>
                    <td style={TD}>{r.exists ? r.provider_label : '—'}</td>
                    <td style={TD}>{r.exists ? r.model : '—'}</td>
                    <td style={TD}>{r.key_broken ? 'не читается' : r.key_set ? r.key_hint : 'нет'}</td>
                    <td style={TD}>{r.exists ? r.daily_limit : '—'}</td>
                    <td style={TD}>
                      <button style={{ ...GHOST, padding: '5px 12px' }}
                              onClick={() => setOpen(open === keyOf(r) ? null : keyOf(r))}>
                        {open === keyOf(r) ? 'Свернуть' : 'Изменить'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.filter(r => keyOf(r) === open).map(r => (
              <EditForm key={keyOf(r)} row={r} presets={data.presets}
                        onClose={() => setOpen(null)} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
